/* JP- Style categories (TNRS-115)
 *
 * <jp-style-categories> progressively enhances the style-category tiles so
 * clicking one filters the collection in place instead of reloading the page:
 * it dispatches the same `facet:criteria-changed` event the "Filters and
 * sort" panel uses, which the theme's <product-facet> element answers by
 * re-rendering the product grid over AJAX (see ProductFacet in theme.js).
 * Without JS, or on a page with no product grid, the tiles stay plain links
 * and fall back to full navigation.
 *
 * The tile whose filter value is currently applied is marked `is-active`;
 * clicking it again clears the filter instead of re-applying it. Active state
 * is re-derived from every `facet:criteria-changed` event on the page, so the
 * tiles also stay in sync when filters change from the panel or "Clear all".
 */
if (!window.customElements.get("jp-style-categories")) {
  class JpStyleCategories extends HTMLElement {
    connectedCallback() {
      this._onCriteriaChanged = this._onCriteriaChanged.bind(this);
      document.addEventListener(
        "facet:criteria-changed",
        this._onCriteriaChanged,
      );
      this.addEventListener("click", this._onTileClicked.bind(this));
      this._markActiveTiles(new URLSearchParams(window.location.search));
    }

    disconnectedCallback() {
      document.removeEventListener(
        "facet:criteria-changed",
        this._onCriteriaChanged,
      );
    }

    _onCriteriaChanged(event) {
      if (!event.detail || !event.detail.url) return;
      this._markActiveTiles(
        new URL(event.detail.url, window.location.origin).searchParams,
      );
    }

    _onTileClicked(event) {
      // Leave modified clicks (new tab etc.) to the browser.
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const tile = event.target.closest("a.jp-style-category");
      if (!tile || !this.contains(tile)) return;

      const productFacet = document.querySelector("product-facet");
      if (!productFacet) return;

      event.preventDefault();

      // Tile hrefs reset every filter to the tile's own value; keep the
      // shopper's current sort order. Clicking the active tile clears the
      // filter again.
      const url = new URL(tile.href, window.location.origin);
      url.hash = "";
      if (tile.classList.contains("is-active")) {
        Array.from(url.searchParams.keys())
          .filter((key) => key.startsWith("filter."))
          .forEach((key) => url.searchParams.delete(key));
      }
      const sortBy = new URLSearchParams(window.location.search).get("sort_by");
      if (sortBy) url.searchParams.set("sort_by", sortBy);

      productFacet.dispatchEvent(
        new CustomEvent("facet:criteria-changed", {
          bubbles: true,
          detail: { url: url.toString() },
        }),
      );
    }

    // A tile is active when every filter param/value pair of its href is
    // present in the given search params.
    _markActiveTiles(searchParams) {
      this.querySelectorAll("a.jp-style-category").forEach((tile) => {
        const tileParams = new URL(tile.href, window.location.origin)
          .searchParams;
        let active = false;
        for (const [key, value] of tileParams.entries()) {
          if (!key.startsWith("filter.")) continue;
          active = searchParams.getAll(key).includes(value);
          if (!active) break;
        }
        tile.classList.toggle("is-active", active);
      });
    }
  }

  window.customElements.define("jp-style-categories", JpStyleCategories);
}
